import React, { useState } from 'react';
import {
  Box,
  Avatar,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert
} from '@mui/material';
import {
  PhotoCamera,
  Upload,
  Delete
} from '@mui/icons-material';
import api from '../../services/api';

interface AvatarUploadProps {
  currentAvatar?: string;
  onAvatarChange: (avatarUrl: string | null) => void;
  size?: number;
  name?: string;
}

const AvatarUpload: React.FC<AvatarUploadProps> = ({
  currentAvatar,
  onAvatarChange,
  size = 100,
  name = ''
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      
      setSelectedFile(file);
      
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('avatar', selectedFile);

      // Upload to backend
      const response = await api.post('/agents/upload-avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Update avatar with the returned URL
      onAvatarChange(response.data.url);

      setIsDialogOpen(false);
      setSelectedFile(null);
      setPreviewUrl(null);
    } catch (error) {
      console.error('Error uploading avatar:', error);
      alert('Failed to upload avatar');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAvatar = () => {
    onAvatarChange(null);
    setIsDialogOpen(false);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <Box sx={{ position: 'relative' }}>
        <Avatar
          src={currentAvatar}
          sx={{ 
            width: size, 
            height: size,
            fontSize: size * 0.4,
            cursor: 'pointer'
          }}
          onClick={() => setIsDialogOpen(true)}
        >
          {name ? getInitials(name) : ''}
        </Avatar>
        <IconButton
          sx={{
            position: 'absolute',
            bottom: -8,
            right: -8,
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
            width: 32,
            height: 32
          }}
          onClick={() => setIsDialogOpen(true)}
        >
          <PhotoCamera fontSize="small" />
        </IconButton>
      </Box>

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Avatar</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 2 }}>
            {/* Current/Preview Avatar */}
            <Avatar
              src={previewUrl || currentAvatar}
              sx={{ width: 120, height: 120, fontSize: 48 }}
            >
              {name ? getInitials(name) : ''}
            </Avatar>

            {/* Upload Options */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="avatar-upload-input"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="avatar-upload-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<Upload />}
                  fullWidth
                >
                  Choose New Photo
                </Button>
              </label>

              {selectedFile && (
                <Alert severity="info">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </Alert>
              )}

              <Typography variant="body2" color="text.secondary" textAlign="center">
                Supported formats: JPG, PNG, GIF (max 5MB)
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
          {currentAvatar && (
            <Button onClick={handleRemoveAvatar} color="error">
              Remove
            </Button>
          )}
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AvatarUpload;